import React from "react";
import { CarOrderPrintProps, OrderDetail } from "../utils/types";
import noImage from "src/assets/Images/imetanic-logo.png";
import { useUser } from "src/business-types/grocery/components/UserContext";

interface ExtendedCarOrderPrintProps extends CarOrderPrintProps {
  latestPayment?: {
    ReceivedAmount: number;
    Paymentmode: string;
    newReceivedAmount: number;
  };
}

const CarOrderPrint: React.FC<ExtendedCarOrderPrintProps> = ({ 
  order, 
  logoUrl, 
  businessArabicName, 
  businessAddreeline, 
  latestPayment 
}) => {
  
  // Helper function to get productDetails from cars array
  const getProductDetails = () => {
    if (!order?.cars) return [];
    
    return order.cars.map(car => {
      // Collect all productDetails from all services in this car
      const allProductDetails: any[] = [];
      let carTotalPrice = 0;

      // Process each service in the car
      (car.services || []).forEach(service => {
        // Check if service has productDetails array
        if (service?.productDetails && Array.isArray(service.productDetails)) {
          service.productDetails.forEach((product: any) => {
            allProductDetails.push({
              product: product?.product || product?.name || "N/A",
              Quantity: product?.Quantity || product?.quantity || 1,
              price: product?.price || product?.amount || 0,
            });
            carTotalPrice += Number(product?.price || product?.amount || 0);
          });
        } else if (service?.product || service?.name) {
          // If service has direct product fields
          allProductDetails.push({
            product: service?.product || service?.name || "N/A",
            Quantity: service?.Quantity || service?.quantity || 1,
            price: service?.price || service?.amount || 0,
          });
          carTotalPrice += Number(service?.price || service?.amount || 0);
        }
      });

      return {
        carNumber: car.carNumber,
        productDetails: allProductDetails,
        totalPrice: carTotalPrice,
      };
    });
  };

  // Calculate total received amount from payment history
  const calculateTotalReceived = (orderData: OrderDetail): number => {
    if (!orderData) return 0;

    if (orderData.paymentHistory && orderData.paymentHistory.length > 0) {
      return orderData.paymentHistory.reduce((sum: number, item) => sum + Number(item?.ReceivedAmount || 0), 0);
    }

    return Number(orderData.payments?.[0]?.ReceivedAmount || 0);
  };

  // Get current payment amount (latest payment)
  const getCurrentPayment = (): number => {
    if (latestPayment?.ReceivedAmount) {
      return latestPayment.ReceivedAmount;
    }

    // If no latest payment, check the last payment in paymentHistory
    if (order?.paymentHistory && order.paymentHistory.length > 0) {
      const lastPayment = order.paymentHistory[order.paymentHistory.length - 1];
      return Number(lastPayment.ReceivedAmount || 0);
    }

    return 0;
  };

  const { user } = useUser();
  const displayLogo = user?.companyLogo || logoUrl;

  const productDetails = getProductDetails();
  const totalAmount = order?.totalOrderPrice || order?.totalAmount || 0;
  const totalReceivedAmount = calculateTotalReceived(order);
  const currentReceivedAmount = getCurrentPayment();
  const remainingAmount = order?.RemainingAmount || 0;

  const isMultiCar = productDetails && productDetails.length > 1;

  // Debug logging
  React.useEffect(() => {
    console.log("Print component data:");
    console.log("Order:", order);
    console.log("Processed productDetails:", productDetails);
    if (order?.cars?.[0]?.services?.[0]) {
      console.log("First service in first car:", order.cars[0].services[0]);
    }
  }, [order, productDetails]);

  return (
    <div
      // className="print-content"
      style={{
        width: "100%",
        minWidth: "80mm",
        maxWidth: "80mm",
        margin: "0 auto",
        padding: "8px 10px 5px 10px",
        fontFamily: "Arial, sans-serif",
        fontSize: "11px",
        lineHeight: "1.1",
        wordWrap: "break-word",
        overflowWrap: "break-word",
        backgroundColor: "white",
        color: "black",
        boxSizing: "border-box",
      }}
    >
      {/* Business Header */}
      <div style={{ textAlign: "center", marginBottom: "10px" }}>
        {displayLogo && (
          <div className="flex justify-center items-center w-full" id="logo-container">
            <img
              src={displayLogo}
              alt="Logo"
              className="w-16 h-16 rounded-full"
              id="business-logo"
              onError={(e) => {
                (e.target as HTMLImageElement).src = noImage;
              }}
            />
          </div>
        )}
        <h2 style={{ margin: "5px 0", fontSize: "18px", fontWeight: "bold", direction: "rtl" }}>{user?.organization_arabic_name}</h2>
        <p style={{ margin: "3px 0", fontSize: "12px" }}>{user?.address_line_1}</p>
      </div>

      {/* Invoice Title */}
      <div
        style={{
          textAlign: "center",
          margin: "4px 0",
          borderBottom: "1px dashed #000",
          paddingBottom: "3px",
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: "12px",
            fontWeight: "bold",
          }}
        >
          INVOICE
        </h3>
      </div>

      {/* Order Info */}
      <div className="flex justify-between mb-2">
        <div style={{ display: "flex", width: "100%" }}>
          <span>Date:</span>
          <span>{order?.Orderdate || "N/A"}</span>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
          <span>Order ID:</span>
          <span>{order?.Orderid || "N/A"}</span>
        </div>
      </div>

      {/* Customer Info */}
      <div className="border-y border-dashed border-black py-2 mb-3">
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
          <span>Customer:</span>
          <span>{order?.CustomerName || "N/A"}</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
          <span>Phone:</span>
          <span>{order?.CustomerPhoneNo || "N/A"}</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span>Payment:</span>
          <span>{latestPayment?.Paymentmode || order?.payments?.[0]?.Paymentmode || "N/A"}</span>
        </div>
        {isMultiCar && (
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: "4px" }}>
            <span>Service Type:</span>
            <span>Multi-Vehicle Service</span>
          </div>
        )}
      </div>

      {/* Products Table */}
      <div className="mb-4 text-sm">
        {/* Table Header */}
        <div className="flex justify-between font-bold border-b border-black pb-1 mb-2">
          <div className="w-3/5">
            Description <br /> وصف
          </div>
          <div className="w-1/5">
            Qty <br /> كمية
          </div>
          <div className="w-1/5 text-right">
            Amount <br /> المبلغ
          </div>
        </div>

        {/* Multi-Car Order Header */}
        {isMultiCar && <div className="text-center font-bold mb-3 p-2 bg-blue-50 border border-blue-200 rounded">MULTI-CAR ORDER - {productDetails.length} VEHICLES</div>}

        {/* Cars & Products */}
        {productDetails.map((carItem, carIndex) => {
          // Skip if no product details for this car
          if (!carItem.productDetails || carItem.productDetails.length === 0) {
            return null;
          }

          return (
            <div key={`car-${carIndex}`} className="mb-4">
              {/* Car Header */}
              <div className="font-bold mb-2 border border-black rounded px-2 py-1 bg-gray-50">
                🚗 Car {carIndex + 1}: {carItem.carNumber || "N/A"}
              </div>

              {/* Product Rows for this car */}
              {carItem.productDetails.map((product, productIndex) => (
                <div key={`product-${productIndex}`} className="flex justify-between mb-1">
                  <div className="w-3/5">{product.product}</div>
                  <div className="w-1/5 text-center">{product.Quantity || 1}</div>
                  <div className="w-1/5 text-right">{product.price || 0} ر.ق</div>
                </div>
              ))}

              {/* Car Subtotal */}
              <div className="flex justify-between mt-2 border-t border-dashed pt-2 font-bold">
                <div>Car {carIndex + 1} Subtotal:</div>
                <div className="text-right">{carItem.totalPrice || 0} ر.ق</div>
              </div>

              {/* Separator between cars */}
              {carIndex < productDetails.length - 1 && <div className="border-b border-dashed border-gray-300 my-3"></div>}
            </div>
          );
        })}

        {/* Show message if no products found */}
        {productDetails.every(car => !car.productDetails || car.productDetails.length === 0) && (
          <div className="text-center py-4 text-gray-500">
            No product details available
          </div>
        )}

        {/* Multi-Car Summary */}
        {isMultiCar && productDetails.some(car => car.productDetails && car.productDetails.length > 0) && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
            <div className="text-center font-bold mb-2">ORDER SUMMARY</div>
            <div className="flex justify-between">
              <span>Total Vehicles:</span>
              <span className="font-bold">{productDetails.length}</span>
            </div>
            <div className="flex justify-between">
              <span>Total Items:</span>
              <span className="font-bold">
                {productDetails.reduce((total, car) => total + (car.productDetails?.length || 0), 0)}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Payment Summary */}
      <div className="border-y border-dashed border-black py-3 mb-3 text-sm">
        {/* Show vehicle count for multi-car orders */}
        {isMultiCar && (
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
            <span>Vehicles:</span>
            <span>{`${productDetails.length} cars`}</span>
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px", fontWeight: "bold" }}>
          <span>Total (المجموع):</span>
          <span>{totalAmount} ر.ق</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
          <span>Total Received (إجمالي المدفوع):</span>
          <span>{totalReceivedAmount} ر.ق</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
          <span>Current Payment (المدفوع حالياً):</span>
          <span>{currentReceivedAmount} ر.ق</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "bold" }}>
          <span>Balance (توازن):</span>
          <span>{remainingAmount} ر.ق</span>
        </div>
      </div>

      {/* Payment History Section */}
      {order?.paymentHistory && order.paymentHistory.length > 0 && (
        <div style={{ width: "100%", marginTop: "10px", marginBottom: "10px" }}>
          <div
            style={{
              borderBottom: "1px dashed #000",
              paddingBottom: "5px",
              marginBottom: "5px",
              fontWeight: "bold",
              fontSize: "11px",
            }}
          >
            Payment History (سجل الدفع)
          </div>
          {order.paymentHistory.map((payment, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "3px",
                fontSize: "10px",
              }}
            >
              <div>
                {payment.Paymentmode} - {order.Orderdate}
              </div>
              <div>{payment.ReceivedAmount} ر.ق</div>
            </div>
          ))}
        </div>
      )}

      {/* Footer */}
      <div
        style={{
          textAlign: "center",
          marginTop: "6px",
          fontSize: "9px",
          borderTop: "1px dashed #000",
          paddingTop: "4px",
        }}
      >
        <p style={{ margin: "2px 0", fontStyle: "italic" }}>Thank you for your business!</p>
        <p style={{ margin: "2px 0" }}>Powered by iMetanic</p>
      </div>
    </div>
  );
};

export default CarOrderPrint;